import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLogger } from '../../../utils/logger';
const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
    const [modules, setModules] = useState([]);
    const [permissionsMap, setPermissionsMap] = useState({});
    const [moduleRoutes, setModuleRoutes] = useState({});
    const [loading, setLoading] = useState(true);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const logger = useLogger("initializePermissions");

    const initializePermissions = () => {
        const storedModules = localStorage.getItem("modules");
        const adminData = localStorage.getItem("adminData");
        if (adminData) {
            try {
                const parsedUserData = JSON.parse(adminData);
                setIsSuperAdmin(parsedUserData?.isSuperAdmin === true);
            } catch (error) {
                logger.error("Failed to parse adminData from localStorage", {
                    error: error.message,
                    stack: error.stack,
                    adminDataLength: adminData?.length,
                    isSuperAdminFallback: false
                });
                setIsSuperAdmin(false);
            }
        }
        if (storedModules) {
            try {
                const modulesData = JSON.parse(storedModules);
                setModules(modulesData);

                const permissions = {};
                const routes = {};

                modulesData.forEach(module => {
                    routes[module.moduleName] = module.route;

                    module.permissions.forEach(permission => {
                        permissions[permission.permission] = true;
                    });
                });
                setPermissionsMap(permissions);
                setModuleRoutes(routes);
            } catch (error) {
                logger.error("Failed to parse modules from localStorage", {
                    error: error.message,
                    stack: error.stack,
                    storedModulesLength: storedModules?.length,
                    storedModulesPreview: storedModules?.substring(0, 100)
                });
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        initializePermissions();
        const handleStorageChange = () => {
            initializePermissions();
        };
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('modules-updated', handleStorageChange);
        window.addEventListener('login', handleStorageChange);
        window.addEventListener('logout', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('modules-updated', handleStorageChange);
            window.removeEventListener('login', handleStorageChange);
            window.removeEventListener('logout', handleStorageChange);
        };
    }, []);

    const hasPermission = (permissionName) => {
        if (isSuperAdmin) return true;
        return permissionsMap[permissionName] === true;
    };



    const hasAnyPermission = (permissionNames) => {
        if (isSuperAdmin) return true;
        if (!permissionNames || permissionNames.length === 0) return true;
        return permissionNames.some(permission => permissionsMap[permission] === true);
    };

    const hasAllPermissions = (permissionNames) => {
        if (isSuperAdmin) return true;
        if (!permissionNames || permissionNames.length === 0) return true;
        return permissionNames.every(permission => permissionsMap[permission] === true);
    };

    const getModule = (moduleName) => {
        return modules.find(module => module.moduleName === moduleName);
    };

    const hasModuleAccess = (moduleName) => {
        if (isSuperAdmin) return true;
        return modules.some(module => module.moduleName === moduleName);
    };

    const getModulePermissions = (moduleName) => {
        const module = getModule(moduleName);
        return module ? module.permissions : [];
    };

    const hasAnyPermissionForModule = (moduleName) => {
        if (isSuperAdmin) return true;
        const module = getModule(moduleName);
        return module ? module.permissions.length > 0 : false;
    };

    const getModuleRoute = (moduleName) => {
        return moduleRoutes[moduleName] || null;
    };

    const getAllAccessibleModules = () => {
        if (isSuperAdmin) return modules;
        return modules.filter(module => module.permissions.length > 0);
    };

    const refreshPermissions = () => {
        initializePermissions();
    };

    const value = {
        modules,
        permissions: permissionsMap,
        moduleRoutes,
        loading,
        isSuperAdmin,

        hasPermission,
        hasAnyPermission,
        hasAllPermissions,

        getModule,
        hasModuleAccess,
        getModulePermissions,
        hasAnyPermissionForModule,
        getModuleRoute,
        getAllAccessibleModules,
        refreshPermissions
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissions must be used within a PermissionProvider');
    }
    return context;
};